import { Editor, Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Node as PMNode } from '@tiptap/pm/model';
import { Range } from '@tiptap/react';
import clsx from 'clsx';

export interface CommentInstance {
  id: string;
  range: Range;
}

export type CommentEvents = {
  enabledChanged: boolean;
  commentSelected: string | null;
  commentHovered: string | null;
  batchCommentRangeUpdated: CommentInstance[];
};

type CommentClasses = {
  base: string;
  comment: string;
  selectedComment: string;
  selection: string;
};

export interface CommentOptions {
  enabled: boolean;
  classes: CommentClasses;
}

export interface CommentStorage {
  enabled: boolean;
  isFocused: boolean;
  selectedCommentId: string | null;
  comments: CommentInstance[];
  selection: Range | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: {
      selectComment: (commentId: string) => ReturnType;
      deselectComment: () => ReturnType;
      deleteComment: (commentId: string) => ReturnType;
      addComment: (comment: CommentInstance) => ReturnType;
      dropComments: () => ReturnType;
      setComments: (comments: CommentInstance[]) => ReturnType;
      enableComments: () => ReturnType;
      disableComments: () => ReturnType;
    };
  }
}

export enum CommentTransactions {
  CommentsUpdated = 'Comment:Updated',
  Enabled = 'Comment:Enabled',
  CommentSelected = 'Comment:Selected',
  CommentPositionsUpdated = 'Comment:PositionsUpdated',
}

function commentDecoration(
  doc: PMNode,
  comments: CommentInstance[],
  selectedComment: string | null,
  classes: CommentClasses,
) {
  const decorations: Decoration[] = comments.map(prob =>
    Decoration.inline(
      prob.range.from,
      prob.range.to,
      {
        class: clsx(classes.base, {
          [classes.comment]: prob.id !== selectedComment,
          [classes.selectedComment]: prob.id === selectedComment,
        }),
        id: prob.id,
      },
      {
        id: prob.id,
        inclusiveLeft: true,
        inclusiveRight: true,
      },
    ),
  );
  return DecorationSet.create(doc, decorations);
}

const Comment = Extension.create<CommentOptions, CommentStorage>({
  name: 'comment',
  addOptions() {
    return {
      enabled: false,
      classes: {
        base: 'comment-plugin',
        comment: 'comment',
        selectedComment: 'selected-comment',
        selection: 'selection',
      },
    };
  },
  addStorage() {
    return {
      enabled: false,
      isFocused: false,
      selectedCommentId: null,
      comments: [],
      selection: null,
    };
  },
  onCreate() {
    // Access and use editor-specific options if needed
    this.storage.enabled = this.options.enabled;
  },
  addCommands() {
    return {
      selectComment:
        commentId =>
        ({ tr }) => {
          if (this.storage.selectedCommentId === commentId) return false;
          this.storage.selectedCommentId = commentId;
          tr.setMeta(CommentTransactions.CommentSelected, commentId);
          tr.setMeta(CommentTransactions.CommentsUpdated, true);
          return true;
        },
      deselectComment:
        () =>
        ({ tr }) => {
          if (this.storage.selectedCommentId === null) return false;
          this.storage.selectedCommentId = null;
          tr.setMeta(CommentTransactions.CommentSelected, null);
          tr.setMeta(CommentTransactions.CommentsUpdated, true);
          return true;
        },
      deleteComment:
        commentId =>
        ({ tr }) => {
          if (!this.storage.comments.find(comment => comment.id === commentId)) return false;
          this.storage.comments = this.storage.comments.filter(comment => comment.id !== commentId);
          tr.setMeta(CommentTransactions.CommentsUpdated, true);
          return true;
        },
      addComment:
        comment =>
        ({ tr }) => {
          if (this.storage.comments.find(c => c.id === comment.id)) return false;
          this.storage.comments.push(comment);
          tr.setMeta(CommentTransactions.CommentsUpdated, true);
          return true;
        },
      dropComments:
        () =>
        ({ tr }) => {
          if (this.storage.comments.length === 0) return false;
          this.storage.comments = [];
          tr.setMeta(CommentTransactions.CommentsUpdated, true);
          return true;
        },
      setComments:
        comments =>
        ({ tr }) => {
          if (this.storage.isFocused) return false;
          this.storage.comments = comments;
          this.storage.selection = null;
          tr.setMeta(CommentTransactions.CommentsUpdated, true);
          return true;
        },
      enableComments:
        () =>
        ({ tr }) => {
          if (this.storage.enabled) return false;
          this.storage.enabled = true;
          tr.setMeta(CommentTransactions.Enabled, true);
          return true;
        },
      disableComments:
        () =>
        ({ tr }) => {
          if (!this.storage.enabled) return false;
          this.storage.enabled = false;
          tr.setMeta(CommentTransactions.Enabled, false);

          return true;
        },
    };
  },
  onFocus() {
    this.storage.isFocused = true;
  },
  onBlur() {
    this.storage.isFocused = false;
  },
  addProseMirrorPlugins() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    return [
      new Plugin<{
        decorators: DecorationSet;
        enabled: boolean;
      }>({
        key: new PluginKey('commentPlugin'),
        props: {
          decorations(state) {
            return this.getState(state)?.decorators;
          },
          handleDOMEvents: {
            click: (_view, event) => {
              const eventTarget = event.target as HTMLElement | null;
              if (!eventTarget || !eventTarget?.classList.contains('comment') || !eventTarget.id) {
                this.editor.commands.deselectComment();
              } else {
                this.editor.commands.selectComment(eventTarget.id);
              }

              return false;
            },
          },
        },
        state: {
          init: () => {
            return { decorators: DecorationSet.empty, enabled: self.storage.enabled };
          },
          apply: (tr, state) => {
            const enabled = tr.getMeta(CommentTransactions.Enabled);
            if (enabled !== undefined) {
              return { decorators: state.decorators, enabled };
            }
            // in case the plugin is not active, return an empty decoration set
            if (!state.enabled) {
              return { decorators: DecorationSet.empty, enabled: state.enabled };
            }

            const commentsUpdated = tr.getMeta(CommentTransactions.CommentsUpdated);
            let decorators: DecorationSet = state.decorators;

            if (tr.docChanged || commentsUpdated) {
              decorators = commentDecoration(
                tr.doc,
                self.storage.comments,
                self.storage.selectedCommentId,
                self.options.classes,
              );
            }

            return { decorators, enabled: state.enabled };
          },
        },
        filterTransaction(tr, state) {
          const pluginState = this.getState(state);
          // NOTE: Skip the filter if the plugin is not enabled.
          if (!pluginState?.enabled) {
            return true;
          }

          const shift = tr.doc.nodeSize - tr.before.nodeSize;
          const hasChange = !tr.before.eq(tr.doc);

          const alertAndSelectComment = (
            commentsToChange: CommentInstance[],
            message = 'You have to rate the comment to be able to edit the highlighted area!',
          ) => {
            tr.setMeta(CommentTransactions.CommentsUpdated, true);
            self.editor.commands.selectComment(commentsToChange[0].id);
            window.alert(message);
          };

          if (hasChange) {
            const changedPos = tr.selection.from - shift;
            const comments = self.storage.comments;
            const commentsToChange = comments.filter(
              comment =>
                (comment.range.from < changedPos && comment.range.to > changedPos) ||
                (shift < 0 && comment.range.to === changedPos),
            );
            if (commentsToChange.length) {
              alertAndSelectComment(commentsToChange);
              return false;
            }
            // This checks for entire highlighted areas being deleted in a bulk deletion
            if (shift < -1) {
              const highlightedAreaInSelection = comments.filter(
                comment =>
                  (comment.range.to! > tr.selection.from &&
                    comment.range.to! < tr.selection.from + Math.abs(shift)) ||
                  (comment.range.from! < tr.selection.from + Math.abs(shift) &&
                    comment.range.from! > tr.selection.from),
              );
              for (const area of highlightedAreaInSelection) {
                const charsToRemove = area.range.to - tr.selection.from;
                if (area.range.to - charsToRemove <= area.range.from) {
                  alertAndSelectComment(
                    [area],
                    'You have to rate the comment to be able to delete the highlighted area!',
                  );
                  return false;
                }
              }
            }
            // This checks for a highlighted area being deleted in a single character deletion
            if (shift === -1) {
              const affectedComment = comments.filter(
                comment => comment.range.from < changedPos && comment.range.to >= changedPos,
              );
              if (
                affectedComment.length &&
                affectedComment[0].range.from === affectedComment[0].range.to + shift
              ) {
                alertAndSelectComment(
                  affectedComment,
                  'You have to rate the comment to be able to remove the highlighted area!',
                );
                return false;
              }
            }

            const updatedComments: CommentInstance[] = [];

            self.storage.comments = comments.map(comment => {
              // This updates the highlighted areas when we remove multiple characters
              if (
                (shift < -1 &&
                  comment.range.to > tr.selection.from &&
                  comment.range.to < tr.selection.from + Math.abs(shift)) ||
                (comment.range.from < tr.selection.from + Math.abs(shift) &&
                  comment.range.from > tr.selection.from)
              ) {
                const charsToRemove = comment.range.to - tr.selection.from;

                const range = {
                  from: comment.range.from,
                  to: comment.range.to - charsToRemove,
                };
                updatedComments.push({ id: comment.id, range });
                // this.storage.commentRangeUpdatedCallbacks.forEach(cb => cb(comment.id, range));

                return {
                  ...comment,
                  range,
                };
              }
              // This shifts each highlighted area outside of the edited region forward or backward
              if (comment.range.from! >= changedPos) {
                const range = {
                  from: comment.range.from + shift,
                  to: comment.range.to + shift,
                };
                updatedComments.push({ id: comment.id, range });
                // this.storage.commentRangeUpdatedCallbacks.forEach(cb => cb(comment.id, range));
                return {
                  ...comment,
                  range,
                };
              }

              return { ...comment };
            });

            tr.setMeta(CommentTransactions.CommentPositionsUpdated, updatedComments);
            tr.setMeta(CommentTransactions.CommentsUpdated, true);
          }
          return true;
        },
      }),
      new Plugin({
        state: {
          init() {
            return { deco: DecorationSet.empty, enabled: self.storage.enabled };
          },
          apply(transaction, state, _prevEditorState, editorState) {
            const enabled = transaction.getMeta(CommentTransactions.Enabled);
            if (enabled !== undefined) {
              return { deco: state.deco, enabled };
            }

            const sel = editorState.selection;
            if (sel) {
              const deco = DecorationSet.create(editorState.doc, [
                Decoration.inline(
                  sel.$from.pos,
                  sel.$to.pos,
                  { class: self.options.classes.selection },
                  {
                    inclusiveLeft: true,
                    inclusiveRight: true,
                  },
                ),
              ]);
              return { deco, enabled: state.enabled };
            }
            return { deco: DecorationSet.empty, enabled: state.enabled };
          },
        },
        props: {
          decorations(state) {
            if (!this.getState(state)?.enabled || self.editor.isEditable) {
              return null;
            }
            if (state && this.getState(state)) {
              return this.getState(state)!.deco;
            }
            return null;
          },
        },
      }),
    ];
  },
});

export const getSelectionFromEditor = (
  editor: Editor,
): { range: Range | null; content: string } => {
  const { selection } = editor.state;
  if (selection.empty) {
    return { range: null, content: '' };
  } else {
    const range = { from: selection.$from.pos, to: selection.$to.pos };
    const content = selection.content().content.textBetween(0, selection.content().content.size);
    return { range, content };
  }
};

export default Comment;
